import { BundlesPermissions, MethodsPermissions, SettingsPermissions, TasksPermissions } from '@campus/dal';
import { AppNavTreeInterface } from '@campus/shared';
import { NavItem } from '@campus/ui';

const isSchoolDetailsRoute = (route: string) => {
  const schoolDetailRouteRegex = /^.*\/schools\/\d+(?:\/.*)?$/;

  return schoolDetailRouteRegex.test(route);
};

const standardSideNavItems: NavItem[] = [
  {
    title: 'dashboard',
    link: '/home',
    icon: 'home',
    requiredPermissions: ['dashboard'],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'dashboard',
    link: '/v2/home',
    icon: 'home',
    requiredPermissions: ['dashboard', SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'methodes',
    icon: 'methods',
    link: '/methods',
    requiredPermissions: [['manageMethods', 'openMethods'], `!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'methodes',
    icon: 'methods',
    link: '/v2/methods',
    requiredPermissions: [
      ['manageMethods', 'openMethods'],
      `!${SettingsPermissions.HIDE_IN_ARCHIVE}`,
      SettingsPermissions.APP_SHELL_V2,
    ],
  },
  {
    title: 'taken',
    icon: 'task',
    link: '/tasks',
    requiredPermissions: [['manageTasks', 'openPolpoTasks'], `!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'taken',
    icon: 'task',
    link: '/v2/tasks',
    requiredPermissions: [
      ['manageTasks', 'openPolpoTasks'],
      `!${SettingsPermissions.HIDE_IN_ARCHIVE}`,
      SettingsPermissions.APP_SHELL_V2,
    ],
  },
  {
    title: 'bundels',
    icon: 'bundles',
    link: '/bundles',
    requiredPermissions: [['manageBundles', 'openBundles'], `!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'bundels',
    icon: 'bundles',
    link: '/v2/bundles',
    requiredPermissions: [
      ['manageBundles', 'openBundles'],
      `!${SettingsPermissions.HIDE_IN_ARCHIVE}`,
      SettingsPermissions.APP_SHELL_V2,
    ],
  },
  {
    title: 'resultaten',
    icon: 'results',
    link: '/resultsV2',
    requiredPermissions: ['manageResultsV2'],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'resultaten',
    icon: 'results',
    link: '/v2/resultsV2',
    requiredPermissions: ['manageResultsV2', SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'mijn scholen',
    link: '/schools',
    requiredPermissions: ['manageSchools'],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
    icon: 'schools',
  },
  {
    title: 'mijn scholen',
    link: '/v2/schools',
    requiredPermissions: ['manageSchools', SettingsPermissions.APP_SHELL_V2],
    icon: 'schools',
    routeFilter: (route: string) => !isSchoolDetailsRoute(route),
  },
  {
    title: 'klassen',
    link: '/v2/schools/{:schoolId}/classgroups',
    requiredPermissions: ['manageSchools', 'manageClassGroups', SettingsPermissions.APP_SHELL_V2],
    icon: 'teacher',
    routeFilter: (route: string) => isSchoolDetailsRoute(route),
  },
  {
    title: 'leerkrachten',
    link: '/v2/schools/{:schoolId}/teachers',
    requiredPermissions: ['manageSchools', 'manageTeachers', SettingsPermissions.APP_SHELL_V2],
    icon: 'teacher',
    routeFilter: (route: string) => isSchoolDetailsRoute(route),
  },
  {
    title: 'leerlingen',
    link: '/v2/schools/{:schoolId}/students/overview',
    requiredPermissions: ['manageSchools', 'manageStudents', SettingsPermissions.APP_SHELL_V2],
    icon: 'student',
    routeFilter: (route: string) => isSchoolDetailsRoute(route),
  },
  {
    title: 'producten',
    link: '/v2/schools/{:schoolId}/products',
    requiredPermissions: ['manageSchools', 'managePurchases', SettingsPermissions.APP_SHELL_V2],
    icon: 'shopping-basket',
    routeFilter: (route: string) => isSchoolDetailsRoute(route),
  },
  {
    title: 'instellingen',
    link: '/v2/schools/{:schoolId}/settings',
    requiredPermissions: ['manageSchools', SettingsPermissions.APP_SHELL_V2],
    icon: 'settings',
    routeFilter: (route: string) => isSchoolDetailsRoute(route),
  },
  {
    title: 'leerlingen / groepen',
    icon: 'assignees',
    link: '/students/overview',
    requiredPermissions: ['students', `!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'leerlingen / groepen',
    icon: 'assignees',
    link: '/v2/students/overview',
    requiredPermissions: ['students', `!${SettingsPermissions.HIDE_IN_ARCHIVE}`, SettingsPermissions.APP_SHELL_V2],
  },
  { title: 'vragen', icon: 'help', link: [`faq`], requiredPermissions: [SettingsPermissions.APP_SHELL_V2] },
];

const standardSettingsNavItems: NavItem[] = [
  {
    title: 'Mijn gegevens',
    icon: 'profile',
    link: '/settings/profile',
    requiredPermissions: [SettingsPermissions.UPDATE_PROFILE],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Mijn gegevens',
    icon: 'profile',
    link: '/v2/settings/profile/general',
    requiredPermissions: [SettingsPermissions.UPDATE_PROFILE, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Wachtwoord',
    icon: 'lock',
    link: '/settings/password/reset',
    requiredPermissions: [SettingsPermissions.UPDATE_PROFILE],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Wachtwoord',
    icon: 'lock',
    link: '/v2/settings/password/reset',
    requiredPermissions: [SettingsPermissions.UPDATE_PROFILE, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Profielfoto',
    icon: 'avatar',
    link: '/settings/profile/avatar',
    requiredPermissions: [SettingsPermissions.UPDATE_AVATAR],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Profielfoto',
    icon: 'avatar',
    link: '/v2/settings/profile/avatar',
    requiredPermissions: [SettingsPermissions.UPDATE_AVATAR, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Mijn koppelingen',
    icon: 'link',
    link: '/settings/credentials',
    requiredPermissions: [SettingsPermissions.ADD_CREDENTIALS],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Mijn koppelingen',
    icon: 'link',
    link: '/v2/settings/credentials',
    requiredPermissions: [SettingsPermissions.ADD_CREDENTIALS, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Mijn scholen',
    icon: 'schools',
    link: '/settings/schools',
    requiredPermissions: [SettingsPermissions.LINK_SCHOOLS],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Mijn scholen',
    icon: 'schools',
    link: '/v2/settings/schools',
    requiredPermissions: [SettingsPermissions.LINK_SCHOOLS, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Mijn leerkrachten',
    icon: 'teacher',
    link: '/settings/coupled-teachers',
    requiredPermissions: [SettingsPermissions.LINK_TEACHERS, SettingsPermissions.UNLINK_TEACHERS],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Mijn leerkrachten',
    icon: 'teacher',
    link: '/v2/settings/coupled-teachers',
    requiredPermissions: [
      SettingsPermissions.LINK_TEACHERS,
      SettingsPermissions.UNLINK_TEACHERS,
      SettingsPermissions.APP_SHELL_V2,
    ],
  },
  {
    title: 'Mijn methodes',
    icon: 'methods',
    link: '/settings/my-methods',
    requiredPermissions: [SettingsPermissions.MANAGE_MY_METHODS],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Mijn methodes',
    icon: 'methods',
    link: '/v2/settings/my-methods',
    requiredPermissions: [SettingsPermissions.MANAGE_MY_METHODS, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Privacy',
    icon: 'terms',
    link: '/settings/profile/termsV2',
    requiredPermissions: [SettingsPermissions.SHOW_PRIVACY_SETTINGS],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Privacy',
    icon: 'terms',
    link: '/v2/settings/profile/termsV2',
    requiredPermissions: [SettingsPermissions.SHOW_PRIVACY_SETTINGS, SettingsPermissions.APP_SHELL_V2],
  },
];

const standardProfileMenuNavItems: NavItem[] = [
  {
    title: 'Profiel',
    icon: 'profile',
    link: '/settings/profile',
    requiredPermissions: ['updateProfile', `!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Profiel',
    icon: 'profile:profile',
    link: '/v2/settings/profile',
    requiredPermissions: ['updateProfile', `!${SettingsPermissions.HIDE_IN_ARCHIVE}`, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Smartschool',
    icon: 'profile:smartschool',
  },
  {
    title: 'Huidig jaar',
    icon: 'unarchive',
    id: 'current-year-selection',
    requiredPermissions: [SettingsPermissions.CAN_VIEW_ARCHIVE],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Huidig jaar',
    icon: 'profile:unarchive',
    id: 'current-year-selection',
    requiredPermissions: [SettingsPermissions.CAN_VIEW_ARCHIVE, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Archiefjaar',
    icon: 'archive',
    id: 'archive-year-selection',
    requiredPermissions: [SettingsPermissions.CAN_VIEW_ARCHIVE],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Archiefjaar',
    icon: 'profile:archive',
    id: 'archive-year-selection',
    requiredPermissions: [SettingsPermissions.CAN_VIEW_ARCHIVE, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Afmelden',
    icon: 'lock',
    link: '/logout',
    dividerBefore: true,
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Afmelden',
    icon: 'profile:logout',
    link: '/v2/logout',
    dividerBefore: true,
    requiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
];
const standardDashboardNavItems: NavItem[] = [
  {
    title: 'Methodes',
    description: 'Bordboeken, lesmateriaal en leerplandoelen openen.',
    link: '/methods',
    icon: 'methods',
    requiredPermissions: [MethodsPermissions.MANAGE_METHODS, `!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Methodes',
    description: 'Bordboeken, lesmateriaal en leerplandoelen openen.',
    link: '/v2/methods',
    icon: 'methods',
    requiredPermissions: [
      MethodsPermissions.MANAGE_METHODS,
      `!${SettingsPermissions.HIDE_IN_ARCHIVE}`,
      SettingsPermissions.APP_SHELL_V2,
    ],
  },
  {
    title: 'Taken',
    description: 'Klassikale en individuele taken klaarzetten.',
    link: '/tasks/manage',
    icon: 'tasks',
    requiredPermissions: [SettingsPermissions.MANAGE_TASKS, `!${SettingsPermissions.HIDE_IN_ARCHIVE}`],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Taken',
    description: 'Klassikale en individuele taken klaarzetten.',
    link: '/v2/tasks/manage',
    icon: 'tasks',
    requiredPermissions: [
      SettingsPermissions.MANAGE_TASKS,
      `!${SettingsPermissions.HIDE_IN_ARCHIVE}`,
      SettingsPermissions.APP_SHELL_V2,
    ],
  },
  {
    title: 'Taken',
    link: '/tasks',
    description: 'Maak taken die voor jou zijn klaargezet.',
    icon: 'tasks',
    requiredPermissions: [TasksPermissions.OPEN_TASKS],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Taken',
    link: '/v2/tasks',
    description: 'Maak taken die voor jou zijn klaargezet.',
    icon: 'tasks',
    requiredPermissions: [TasksPermissions.OPEN_TASKS, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Resultaten',
    description: 'Resultaten van oefeningen per taak, bundel of per leerling raadplegen en exporteren.',
    icon: 'results',
    link: '/resultsV2',
    requiredPermissions: [SettingsPermissions.MANAGE_RESULTS_V2],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Resultaten',
    description: 'Resultaten van oefeningen per taak, bundel of per leerling raadplegen en exporteren.',
    icon: 'results',
    link: '/v2/resultsV2',
    requiredPermissions: [SettingsPermissions.MANAGE_RESULTS_V2, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Bundels',
    description: 'Maak bundels die voor jou zijn klaargezet.',
    link: '/bundles',
    icon: 'bundles',
    requiredPermissions: [BundlesPermissions.OPEN_BUNDLES],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Bundels',
    description: 'Maak bundels die voor jou zijn klaargezet.',
    link: '/v2/bundles',
    icon: 'bundles',
    requiredPermissions: [BundlesPermissions.OPEN_BUNDLES, SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Methodes',
    description: 'Bordboeken, lesmateriaal en leerplandoelen openen.',
    link: '/methods',
    icon: 'methods',
    requiredPermissions: [MethodsPermissions.OPEN_METHODS],
    hideWhenRequiredPermissions: [SettingsPermissions.APP_SHELL_V2],
  },
  {
    title: 'Methodes',
    description: 'Bordboeken, lesmateriaal en leerplandoelen openen.',
    link: '/v2/methods',
    icon: 'methods',
    requiredPermissions: [MethodsPermissions.OPEN_METHODS, SettingsPermissions.APP_SHELL_V2],
  },
];

export interface AppConfigInterface {
  appNavtree: AppNavTreeInterface;
}
export const polpoConfig: AppConfigInterface = {
  appNavtree: {
    dashboardNav: standardDashboardNavItems,
    sideNav: standardSideNavItems,
    settingsNav: standardSettingsNavItems,
    profileMenuNav: standardProfileMenuNavItems,
  },
};
